import { Box, BoxProps, Card, Chip, ChipProps, Grid, Link, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { JamezzStylesCSSInput } from "./JamezzStylesCSSInput";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FolderIcon from "@mui/icons-material/Folder";
import TranslationWrapper from "../../../../../components/TranslationWrapper/TranslationWrapper";
import { makeStyles } from "tss-react/mui";
import { CustomizableComponent, SxProperties } from "../CustomComponents";
import { ImageResolutionHint } from "../../../../../components/ImageHints/ImageResolutionHint";
import FormFile from "../../../../../components/Forms/FormFile";
import _ from "lodash";
import { parseJSClassName } from "./JSClasses";

/**
 *
 */
const cssPropertyDisplayName: { [key in SxProperties]?: string } = {
  translate: "Move",
};

const useStyles = makeStyles()({
  /**
   * Selection ribbon styles
   **/
  inputName: {
    padding: 5,
    color: "rgba(0, 0, 0, 0.54)",
    fontWeight: 400,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "1rem",
  },
  backgroundMapBar: {
    display: "grid",
    gridTemplateColumns: "64px auto",
    paddingTop: 5,
    paddingBottom: 10,
    paddingLeft: 25,
    paddingRight: 25,
    borderRadius: 4,
    transition: "0.3s",
    cursor: "pointer",
    justifyItems: "start",
    alignItems: "baseline",
  },
  dropdownArrow: {
    marginRight: 8,
    transition: "0.1s",
  },
});

function camelCaseToHumanReadable(str: string) {
  return str
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .replace(/-/g, " >")
    .replace(/./, (char) => char.toUpperCase());
}

function getComponent(
  components: CustomizableComponent[] | Record<string, CustomizableComponent>,
  index: number | string
): CustomizableComponent {
  return (components as any)[index];
}

const simpleModeEditableValues: SxProperties[] = [
  "color",
  "backgroundColor",
  "fontFamily",
  "fontSize",
  "textDecoration",
  "fontStyle",
  "fontWeight",
];

/**
 * A reusable badge component with a clear "name" and "value" distinction
 * It's useful to display key-value pairs, ie "group" with its "groupname"
 * @param name
 * @param value
 * @param textColor
 * @param badgeColor
 * @param params
 * @param chipColor
 * @constructor
 */
export function LabeledBadge({
  name = "Name",
  value,
  textColor = "#222",
  badgeColor = { backgroundColor: "#bbb" },
  chipColor = "default",
  params = {},
}: {
  name?: string;
  value: string;
  textColor?: string;
  badgeColor?: { backgroundColor: string };
  chipColor?: ChipProps["color"];
  params?: ChipProps;
}) {
  return (
    <Chip
      color={chipColor}
      variant={"filled"}
      {...params}
      icon={
        <Box
          component={"span"}
          sx={{
            fontWeight: "800",
            padding: "0 8px",
            borderRadius: "12px",
            backgroundColor: badgeColor.backgroundColor,
            color: textColor,
            textTransform: "capitalize",
            fontVariantCaps: "all-small-caps",
          }}
        >
          {camelCaseToHumanReadable(name)}
        </Box>
      }
      label={
        <span>
          <span
            style={{
              color: textColor,
              fontSize: "0.9em",
              textTransform: "capitalize",
              fontVariantCaps: "all-small-caps",
            }}
          >
            {camelCaseToHumanReadable(value)}
          </span>
        </span>
      }
      sx={{
        mr: 1,
      }}
    />
  );
}

export function ParameterBadge(props: { JSClass: string } | { value: number; parameterName: string }) {
  const value = "value" in props ? props.value : parseJSClassName(props.JSClass).parameter;
  const parameterName = "parameterName" in props ? props.parameterName : parseJSClassName(props.JSClass).parameterName;
  if (!value) return null;
  return (
    <LabeledBadge
      badgeColor={{ backgroundColor: "#005f95" }}
      chipColor={"info"}
      name={parameterName}
      textColor={"white"}
      value={value.toString()}
    />
  );
}

export function JSGroupBadge({ JSClass }: { JSClass: string }) {
  const group = parseJSClassName(JSClass).group;
  return <LabeledBadge name={"Group"} value={group} />;
}

export function JSOwnNameBadge({ JSClass }: { JSClass: string }) {
  const name = parseJSClassName(JSClass).ownName;
  return <LabeledBadge name={"Name"} value={name} />;
}

export function _JamezzStylesComponent<T extends CustomizableComponent[] | Record<string, CustomizableComponent>>({
  showAdminValues,
  components,
  index,
  translatedLabelName,
  startOpened,
  extraProps,
  editMode,
}: {
  showAdminValues: boolean;
  translatedLabelName: string;
  startOpened: boolean;
  extraProps?: BoxProps;
  components: T;
  index: T extends CustomizableComponent[] ? number : string;
  editMode: "simple" | "advanced";
}) {
  const { classes } = useStyles();
  const component = getComponent(components, index);
  const [isOpened, setIsOpened] = useState(false);
  const [showInputsWithValue, setShowInputsWithValue] = useState(true);
  const className = component.className.substring(1, component.className.length);
  const customComponentStyling = useWatch({ name: "custom_component_styling" });
  const customFonts: { fontFamily: string; [k: string]: unknown }[] = useWatch({ name: "custom_fonts" }) ?? [];
  const themeFont = useWatch({ name: "extra_settings__mui_theme_typography_font_family" });
  const fonts: string[] = [themeFont, ...new Set(customFonts.map((f) => f.fontFamily))];

  let fullInputAmount = 0;
  let emptyInputAmount = 0;

  const editValues =
    editMode === "simple" ? _.pick(component.values ?? {}, simpleModeEditableValues) : (component.values ?? {});

  Object.keys(editValues).map((valueKey) => {
    const inputValue = customComponentStyling?.[className]?.[valueKey];
    if (!inputValue) {
      emptyInputAmount++;
    } else {
      fullInputAmount++;
    }
  });

  const allInputs = () => {
    return Object.keys(editValues).map((valueKey) => {
      const inputValue = customComponentStyling?.[className]?.[valueKey];
      if (!(inputValue == null || !inputValue) || isOpened) {
        return (
          <Box
            key={valueKey}
            style={{
              padding: 5,
              paddingBottom: 15,
            }}
          >
            <Typography className={classes.inputName}>
              {valueKey in cssPropertyDisplayName ? cssPropertyDisplayName[valueKey as SxProperties] : valueKey}
            </Typography>
            <JamezzStylesCSSInput JSClassName={component.className} fonts={fonts} valueKey={valueKey as SxProperties} />
          </Box>
        );
      }
    });
  };

  const checkInputs = () => {
    if (isOpened && emptyInputAmount > 0 && fullInputAmount > 0) {
      setIsOpened(false);
    } else if (isOpened && emptyInputAmount === 0 && fullInputAmount > 0) {
      setShowInputsWithValue(false);
      setIsOpened(false);
    } else if (isOpened && emptyInputAmount > 0 && fullInputAmount === 0) {
      setIsOpened(false);
    } else if (!isOpened) {
      setIsOpened(true);
      setShowInputsWithValue(true);
    }
  };

  useEffect(() => {
    if (emptyInputAmount > 0 && fullInputAmount > 0) {
      setIsOpened(false);
    } else if (emptyInputAmount === 0 && fullInputAmount > 0) {
      setShowInputsWithValue(true);
      setIsOpened(true);
    } else if (emptyInputAmount > 0 && fullInputAmount === 0) {
      setIsOpened(false);
    } else {
      console.log("Error, No passed value");
    }
    if (startOpened) {
      setIsOpened(true);
    }
  }, []);

  return (
    <Box className={component.className} id={`customComponent`} {...extraProps}>
      <Card variant={"outlined"}>
        <div
          className={classes.backgroundMapBar}
          onClick={checkInputs}
          style={{
            backgroundColor: isOpened ? "#fafafa" : "white",
          }}
        >
          <div>
            <ArrowDropDownIcon
              className={classes.dropdownArrow}
              style={{
                transform: isOpened ? "translateY(5px) rotate(0deg)" : "translateY(5px) rotate(-90deg)",
              }}
            />
            <FolderIcon
              fontSize={"small"}
              style={{
                transform: "translateY(5px)",
                marginRight: 5,
              }}
            />
          </div>
          <div>
            {showAdminValues ? (
              <span
                style={{
                  fontSize: "1rem",
                  display: "inline-flex",
                  color: translatedLabelName.includes("[") && translatedLabelName.includes("]") ? "black" : "blue",
                }}
              >
                <TranslationWrapper tDefault={component.label} tKey={"CustomComponents." + component.label} />
              </span>
            ) : (
              <span>{component.label}</span>
            )}
          </div>
          <div style={{ gridColumn: "2 / span 2" }}>
            <Tooltip title={component.className}>
              <div>
                <JSGroupBadge JSClass={component.className} />
                <JSOwnNameBadge JSClass={component.className} />
                <ParameterBadge JSClass={component.className} />
              </div>
            </Tooltip>
          </div>
        </div>
        <Grid item style={{ marginLeft: 70 }} xs={12}>
          <Box>
            {component.JSImage
              ? component.JSImage.map((JSImage) => {
                  return (
                    <React.Fragment key={JSImage.name}>
                      <Grid
                        container
                        item
                        style={{
                          padding: 5,
                          paddingBottom: 15,
                        }}
                      >
                        <Grid item xs={12}>
                          <Typography className={classes.inputName}>{JSImage.name}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <ImageResolutionHint res={JSImage.res} />
                          <FormFile
                            canCrop={false}
                            name={`extra_settings__${JSImage.name}`}
                            nullable={true}
                            type={"file"}
                          />
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  );
                })
              : null}
          </Box>
          <Box>{showInputsWithValue ? allInputs() : null}</Box>
          {!((emptyInputAmount === 0 && fullInputAmount > 0) || (emptyInputAmount > 0 && fullInputAmount === 0)) &&
          !isOpened ? (
            <Link
              component={"button"}
              onClick={() => {
                if (!isOpened) {
                  setIsOpened(true);
                }
              }}
              style={{
                display: "flex",
                padding: 10,
              }}
              variant={"body2"}
            >
              {/*TODO: Translate*/}
              Show more options
            </Link>
          ) : null}
        </Grid>
      </Card>
    </Box>
  );
}

export const JamezzStylesComponent = React.memo(_JamezzStylesComponent);
